import get from 'lodash/get';
import { metricKeys } from '@pulse/metrics';
import { dataFilterMetricsList } from '@utils';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import metricsStore from '@src/globalStore/metricsStore';
import {
  METRIC_SWITCH_MAP,
  INVERTED_METRIC_SWITCH_MAP,
  INVERTED_LOCAL_METRIC_SWITCH_MAP,
} from './metricsMeta';
import { BPS, PERCENTAGE } from './constants';

interface MetricUtilsInputTypes {
  metricKey: string;
  context?: string;
  defaultValue?: any;
  hasSubscript?: boolean;
}

export const getMetricDisplayName = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = metricKey,
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  if (!combinedMetricsMeta) return defaultValue;
  return (
    get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.displayName`) || defaultValue
  );
};

export const getMetricSubscript = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = '',
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  return (
    get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.subscript`) || defaultValue
  );
};

export const getMetricOvbDisplayName = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = metricKey,
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  return (
    get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.ovbDisplayName`) || defaultValue
  );
};

export const getMetricHelpText = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = '',
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  return get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.helpText`) || defaultValue;
};

export const getMetricMetaByKey = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = {},
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  return get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}`) || defaultValue;
};

export const shouldCalcPercentage = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = false,
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  return (
    get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.calcPercentage`) || defaultValue
  );
};

// returns metric unit name
const getMetricUnit = ({
  metricKey,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = '',
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  return get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.unit`) || defaultValue;
};

export const getMetricShortName = ({
  metricKey,
  hasSubscript = false,
  context = CURRENT_PRODUCT_MODE,
  defaultValue = {
    short: metricKey,
    subScript: '',
  },
}: MetricUtilsInputTypes) => {
  const combinedMetricsMeta = metricsStore.getState().combinedMetricsMeta;
  const metricName =
    get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.short`) || defaultValue.short;
  if (!hasSubscript) {
    return metricName;
  }
  const subscript =
    get(combinedMetricsMeta, `${context.toLowerCase()}.${metricKey}.subscript`) ||
    defaultValue.subScript;
  return subscript ? `${metricName} ${subscript}` : metricName;
};

export const getMetricNameByUnit = (metricKey, unit) => {
  let metricByUnit = metricKey;
  if ((unit === '%' || unit === PERCENTAGE) && !isPercentageMetric(metricKey)) {
    metricByUnit = getPercentageMetric(metricKey);
  }
  return metricKeys[metricByUnit] || metricKey;
};

export const getMetricUnitText = (unit) => {
  if (unit === BPS) {
    return BPS;
  }
  return ` ${unit}`;
};

// check if a metric is percentage
const isPercentageMetric = (metricKey) => getMetricUnit({ metricKey }) === '%';

// combine with de-duplication
const combineWithMetricDeDuplication = (sourceList, items) =>
  Array.from(new Set([...sourceList, ...items]));

// exclude with de-duplication
const excludeWithMetricDeDuplication = (sourceList, items) => {
  const sourceSet = new Set(sourceList);
  items.forEach((i) => sourceSet.delete(i));
  return Array.from(sourceSet);
};

// generate a new metric list based on:
// source: source array
// changeObj: include and exclude arrays in an object
const genMetricList = (source, changeObj) => {
  // include
  const metrics = combineWithMetricDeDuplication(source, changeObj.include);
  // exclude
  return excludeWithMetricDeDuplication(metrics, changeObj.exclude);
};

// get metric exclude list for current preset
const getMetricExcludeList = (presetFid) => {
  if (dataFilterMetricsList[presetFid] !== undefined) {
    return dataFilterMetricsList[presetFid].exclude;
  }
  return [];
};

// check if a metric or it's toggle metric exists in a list
const isMetricIncluded = (sourceList, metricKey) => {
  const sourceSet = new Set(sourceList);
  const mappedMetric = INVERTED_METRIC_SWITCH_MAP[metricKey] || METRIC_SWITCH_MAP[metricKey];
  return sourceSet.has(metricKey) || sourceSet.has(mappedMetric);
};

// return percetage metric if present
// for timeseries
const getPercentageMetricForSeries = (metricKey) => {
  // backend/gateway percentage metric
  if (metricKey in INVERTED_METRIC_SWITCH_MAP) {
    return INVERTED_METRIC_SWITCH_MAP[metricKey];
  }
  return metricKey;
};

// return percetage metric if present
// for dimension tables
const getPercentageMetric = (metricKey: string): string => {
  // backend/gateway percentage metric
  if (metricKey in INVERTED_METRIC_SWITCH_MAP) {
    return INVERTED_METRIC_SWITCH_MAP[metricKey];
  }
  // frontend/local percentage metric
  if (metricKey in INVERTED_LOCAL_METRIC_SWITCH_MAP) {
    return INVERTED_LOCAL_METRIC_SWITCH_MAP[metricKey];
  }
  return metricKey;
};

export {
  getMetricUnit,
  isPercentageMetric,
  getMetricExcludeList,
  combineWithMetricDeDuplication,
  excludeWithMetricDeDuplication,
  genMetricList,
  isMetricIncluded,
  getPercentageMetric,
  getPercentageMetricForSeries,
};
